import { getApiBaseUrl } from "@/lib/api-base-url";
import { isFutureCohort } from "@/features/home/lib/homepage-format";
import { resolveCardPrice, type CardPrice, type CardPriceInput } from "@/features/courses/lib/card-price";

/**
 * `SITE-NAV01`: the More panel's "next live cohort" card, from the course
 * record and nothing else (the featured courses' `nextCohortStartAt`, the
 * earliest pricing plan cohort the API derives, and their card price).
 *
 * Read from the browser, once, the first time the visitor reaches for More
 * (the pointer over it, focus on it, a tap), never on page load: the panel is
 * the only thing that shows it, and the bar must not cost every page a request.
 * A failed read is forgotten, so the next open asks again, and the panel shows
 * the Learn C card instead in the meantime.
 */

export type NextCohort = {
  slug: string;
  title_en: string;
  title_bn: string | null;
  startAt: string;
  price: CardPrice;
};

type FeaturedCourse = CardPriceInput & {
  slug?: unknown;
  title_en?: unknown;
  title_bn?: unknown;
  nextCohortStartAt?: string | null;
  learnTrack?: boolean;
};

/** The featured course whose cohort starts soonest, among those still ahead; a Learn track has no cohort. */
export function pickNextCohort(courses: readonly FeaturedCourse[], now: Date = new Date()): NextCohort | null {
  let best: NextCohort | null = null;
  for (const course of courses) {
    if (course.learnTrack) continue;
    if (typeof course.slug !== "string" || typeof course.title_en !== "string" || !course.price) continue;
    const startAt = course.nextCohortStartAt;
    if (!isFutureCohort(startAt, now)) continue;
    if (best && new Date(best.startAt).getTime() <= new Date(startAt!).getTime()) continue;
    best = {
      slug: course.slug,
      title_en: course.title_en,
      title_bn: typeof course.title_bn === "string" && course.title_bn.trim() ? course.title_bn : null,
      startAt: startAt!,
      price: resolveCardPrice(course),
    };
  }
  return best;
}

/**
 * Calendar days from today to the start day, on the visitor's own calendar:
 * a class on 2 October is "in 7 days" all through 25 September, and 0 on the
 * day itself. Counting hours instead said "in 8 days" on the 25th for an
 * evening start (found on production, 2026-09-25).
 */
export function daysUntil(iso: string, now: Date = new Date()): number {
  const start = new Date(iso);
  const startDay = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.round((startDay - today) / 86_400_000));
}

let pending: Promise<NextCohort | null> | null = null;

export function loadNextCohort(): Promise<NextCohort | null> {
  pending ??= fetch(`${getApiBaseUrl()}/v1/catalog/featured-courses?limit=6`, { credentials: "omit" })
    .then(async (res) => {
      if (!res.ok) throw new Error(`featured-courses ${res.status}`);
      const json = (await res.json()) as { data?: { courses?: unknown } };
      const courses = Array.isArray(json.data?.courses) ? (json.data.courses as FeaturedCourse[]) : [];
      return pickNextCohort(courses);
    })
    .catch(() => {
      pending = null;
      return null;
    });
  return pending;
}
